/**
 * AppICEMAP — Auth API
 * Login, register, logout, token refresh, and current user.
 */

import { isAdmin } from "../utils/permissions.js";
import { clearCache } from "../services/cache.js";
import {
	DIRECTUS_URL,
	getClient,
	setSession,
	clearSession,
	readItems,
} from "./directus.js";
import {
	createDirectus,
	rest,
	authentication,
	readMe,
	refresh,
	staticToken,
	registerUser,
} from "@directus/sdk";

/**
 * Turn a Directus/SDK failure into a message that names the actual problem.
 *
 * When the request never reaches Directus (wrong base URL, a proxy that
 * rejects the method, a stale service worker answering it) the "errors" field
 * carries an HTML error page instead of the usual JSON array, and logging it
 * raw just dumps markup.
 *
 * @param {any} err
 * @param {string} path - API path attempted, e.g. "/auth/login"
 * @returns {string}
 */
function describeApiError(err, path) {
	const raw = err?.errors;

	if (typeof raw === "string" && raw.includes("<html")) {
		const status = err?.response?.status;
		const title = raw.match(/<title>([^<]*)<\/title>/i)?.[1] || "página HTML";
		return (
			`El servidor devolvió ${title}${status ? ` (${status})` : ""} en vez de JSON al llamar a ${DIRECTUS_URL}${path}. ` +
			"La petición no llegó a Directus: revisa VITE_DIRECTUS_URL (se compila en el bundle, hay que reconstruir tras cambiarla), " +
			"el proxy que hay delante de la API, y si un service worker antiguo está interceptando la petición."
		);
	}

	return raw?.[0]?.message || err?.message || String(err);
}

/**
 * Login with email and password.
 * @param {string} email
 * @param {string} password
 * @returns {Promise<object>} User data
 */
export async function login(email, password) {
	const authClient = createDirectus(DIRECTUS_URL)
		.with(rest())
		.with(authentication("json"));

	try {
		const result = await authClient.login(email, password, { mode: "json" });

		// Directus returns `expires` in MILLISECONDS (access-token TTL, ~15 min).
		setSession({
			access_token: result.access_token,
			refresh_token: result.refresh_token,
			expires_at: Date.now() + (result.expires || 15 * 60 * 1000),
			// Refresh token is valid much longer (default 7 days) — keep the
			// session usable that long by refreshing the access token.
			refresh_expires_at: Date.now() + 7 * 24 * 60 * 60 * 1000,
		});

		// Fetch user profile
		const user = await getMe();
		return user;
	} catch (err) {
		const detail = describeApiError(err, "/auth/login");
		console.error("[Auth] Login error:", detail);
		throw err;
	}
}

/**
 * Register a new user account.
 *
 * `directus_users` is a CORE collection — Directus rejects `createItem` on it
 * outright ("Cannot use createItem for core collections"), which is exactly
 * what broke sign-up. Public self-registration has its own dedicated
 * endpoint (`POST /users/register`, wrapped here by the SDK's `registerUser`)
 * that Directus only enables when the project has "Public Registration" on —
 * this instance does — and it deliberately doesn't accept a `role`: the
 * account gets whatever "Default Role for New Users" the project is
 * configured with, so a signup request can't hand itself a role.
 *
 * @param {{ email: string, password: string, first_name?: string, last_name?: string }} data
 * @returns {Promise<void>} Directus returns no body on success — the caller
 *   (RegisterScreen) already logs in right after to obtain a session.
 */
export async function register(data) {
	const publicClient = createDirectus(DIRECTUS_URL).with(rest());

	try {
		await publicClient.request(
			registerUser(data.email, data.password, {
				first_name: data.first_name || undefined,
				last_name: data.last_name || undefined,
			}),
		);
	} catch (err) {
		console.error(
			"[Auth] Register error:",
			describeApiError(err, "/users/register"),
		);
		throw err;
	}
}

/**
 * Logout and clear session.
 */
export async function logout() {
	try {
		const client = getClient();
		// Directus SDK logout if authenticated
		if (client) {
			try {
				const authClient = createDirectus(DIRECTUS_URL)
					.with(rest())
					.with(authentication("json"));
				await authClient.logout();
			} catch {
				/* Best effort */
			}
		}
	} finally {
		stopAuthAutoRefresh();
		clearSession();

		// Los reportes cacheados pueden ser sensibles en un dispositivo
		// compartido: al cerrar sesión no deben quedar en el disco.
		clearCache();
	}
}

/**
 * Refresh the access token using the stored refresh token, via the Directus
 * REST endpoint directly (the SDK's `refresh()` needs its own token storage,
 * which we don't use — hence the previous implementation never worked).
 *
 * @returns {Promise<boolean>} true if the session was refreshed.
 *   Returns false without clearing on transient/network errors; clears the
 *   session only when the refresh token is definitively rejected (401/403).
 */
export async function refreshSession() {
	const { getSession } = await import("./directus.js");
	const session = getSession();
	if (!session?.refresh_token) return false;

	try {
		const res = await fetch(`${DIRECTUS_URL}/auth/refresh`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				refresh_token: session.refresh_token,
				mode: "json",
			}),
		});

		if (res.status === 401 || res.status === 403) {
			// Refresh token invalid/expired → genuine logout
			clearSession();
			return false;
		}
		if (!res.ok) return false; // transient (5xx) — keep session, retry later

		const body = await res.json();
		const data = body.data || body;
		if (!data.access_token) return false;

		setSession({
			access_token: data.access_token,
			refresh_token: data.refresh_token || session.refresh_token,
			expires_at: Date.now() + (data.expires || 15 * 60 * 1000),
			refresh_expires_at: Date.now() + 7 * 24 * 60 * 60 * 1000,
		});
		return true;
	} catch {
		// Network error — do NOT clear; the session may still be valid
		return false;
	}
}

/** Back-compat alias. */
export const refreshToken = refreshSession;

/** @type {number | null} */
let _refreshTimer = null;

/**
 * Keep the access token fresh: refresh when it's within 2 min of expiry,
 * checked every minute and also on reconnect / tab focus. Idempotent.
 */
export function startAuthAutoRefresh() {
	stopAuthAutoRefresh();

	const tick = async () => {
		const { isAuthenticated, isAccessTokenExpired } = await import(
			"./directus.js"
		);
		if (isAuthenticated() && isAccessTokenExpired(2 * 60 * 1000)) {
			await refreshSession();
		}
	};

	_refreshTimer = setInterval(tick, 60_000);
	window.addEventListener("online", tick);
	document.addEventListener("visibilitychange", () => {
		if (!document.hidden) tick();
	});
	tick();
}

/** Stop the auto-refresh timer. */
export function stopAuthAutoRefresh() {
	if (_refreshTimer !== null) {
		clearInterval(_refreshTimer);
		_refreshTimer = null;
	}
}

/** Profile fields every Directus version has */
const ME_FIELDS = [
	"id",
	"email",
	"first_name",
	"last_name",
	"avatar",
	"karma",
	"strikes",
	"rehabilitations",
	"is_limited",
];

/**
 * Admin-detection field sets, tried in order.
 *
 * Directus silently DROPS fields it doesn't know instead of failing: asking for
 * `role.admin_access` (which only exists up to Directus 10) on an 11.x instance
 * makes the whole response collapse to `{ id }` — no error to catch. That is
 * why these never travel with the profile request, and why each shape is asked
 * for separately.
 *
 * In Directus 11 `admin_access` lives on the policies attached to the ROLE,
 * which is why the role path comes first.
 */
const ADMIN_FIELD_SETS = [
	// Directus 11+
	[
		"role.id",
		"role.name",
		"role.policies.policy.admin_access",
		"policies.policy.admin_access",
	],
	// Directus 10
	["role.id", "role.name", "role.admin_access"],
];

/**
 * Get the currently authenticated user's profile.
 * @returns {Promise<object | null>}
 */
export async function getMe() {
	const client = getClient();

	try {
		// Use readMe from the auth-enabled client
		const authClient = createDirectus(DIRECTUS_URL)
			.with(rest())
			.with(authentication("json"));

		const { getSession } = await import("./directus.js");
		const session = getSession();
		if (!session) return null;

		const authed = authClient.with(staticToken(session.access_token));

		// The profile comes first, on its own. Asking for it together with the
		// role/policy fields meant that any complaint about those (they only
		// exist in some Directus versions) took the whole request down with it,
		// leaving the app without name or email.
		const user = await authed.request(readMe({ fields: ME_FIELDS }));

		// Admin detection is a best-effort extra: whatever happens here, the
		// profile above is already complete.
		for (const fields of ADMIN_FIELD_SETS) {
			try {
				const extra = await authed.request(readMe({ fields }));
				const merged = { ...user, ...extra };
				if (isAdmin(merged)) return merged;
				// Shape understood but the user simply isn't an admin
				if (extra?.role) return merged;
			} catch {
				// Try the next shape
			}
		}

		return user;
	} catch (err) {
		console.error("[Auth] getMe error:", err);
		return null;
	}
}
